import { useLocation } from 'react-router-dom';
import { useBranch } from '../../contexts/BranchContext';
import { normalizeVisualTheme } from '../../lib/visualThemes';

/** Emoji bay lơ lửng theo giao diện lễ hội đang bật */
const THEME_AMBIENT: Record<string, { emoji: string; glow: string }[]> = {
  midautumn: [
    { emoji: '🏮', glow: 'rgba(245,158,11,0.8)' },
    { emoji: '🌕', glow: 'rgba(253,230,138,0.8)' },
    { emoji: '🥮', glow: 'rgba(217,119,6,0.8)' },
  ],
  halloween: [
    { emoji: '🦇', glow: 'rgba(168,85,247,0.8)' },
    { emoji: '🎃', glow: 'rgba(249,115,22,0.8)' },
    { emoji: '👻', glow: 'rgba(226,232,240,0.8)' },
  ],
  christmas: [
    { emoji: '❄️', glow: 'rgba(191,219,254,0.8)' },
    { emoji: '🎁', glow: 'rgba(220,38,38,0.8)' },
    { emoji: '🔔', glow: 'rgba(250,204,21,0.8)' },
  ],
};

export default function SpecialEffects() {
  const location = useLocation();
  const { currentBranch } = useBranch();

  // Don't show effects on admin pages
  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  const visualTheme = normalizeVisualTheme(currentBranch?.visual_theme);
  const effectType = currentBranch?.effect_type || 'none';
  const effectIntensity = currentBranch?.effect_intensity ?? 30;

  // Calculate number of particles based on intensity
  const particleCount = Math.floor((effectIntensity / 100) * 50);

  const ambient = THEME_AMBIENT[visualTheme];

  return (
    <>
      {/* Hiệu ứng nền của giao diện lễ hội (Trung Thu / Halloween / Giáng sinh) */}
      {ambient && (
        <div className="fixed inset-0 pointer-events-none z-[1] overflow-hidden">
          {ambient.map((item, groupIndex) =>
            [...Array(2)].map((_, i) => (
              <div
                key={`${visualTheme}-${groupIndex}-${i}`}
                className="absolute special-float-up"
                style={{
                  left: `${8 + ((groupIndex * 2 + i) * 15) % 84}%`,
                  animationDelay: `${groupIndex * 1.8 + i * 3.4}s`,
                  animationDuration: `${11 + groupIndex * 2 + i}s`,
                  bottom: '-50px',
                }}
              >
                <div
                  className="text-3xl md:text-4xl opacity-70"
                  style={{ filter: `drop-shadow(0 0 8px ${item.glow})` }}
                >
                  {item.emoji}
                </div>
              </div>
            )),
          )}
        </div>
      )}

      {/* Snow Effect - Tuyết rơi */}
      {effectType === 'snow' && particleCount > 0 && (
        <div className="fixed inset-0 pointer-events-none z-[1] overflow-hidden">
          {[...Array(particleCount)].map((_, i) => {
            const startX = Math.random() * 100;
            const endX = startX + (Math.random() * 100 - 50);
            const delay = Math.random() * 8;
            const duration = 8 + Math.random() * 6;
            return (
              <div
                key={`snow-${i}`}
                className="absolute"
                style={{
                  left: `${startX}%`,
                  top: '-100px',
                  animation: `special-snow-fall-${i} ${duration}s linear ${delay}s infinite`,
                }}
              >
                <div className="text-xl md:text-2xl opacity-90">❄️</div>
                <style>{`
                  @keyframes special-snow-fall-${i} {
                    0% {
                      transform: translateY(0) translateX(0) rotate(0deg);
                      opacity: 0;
                    }
                    5% {
                      opacity: 0.9;
                    }
                    95% {
                      opacity: 0.9;
                    }
                    100% {
                      transform: translateY(calc(100vh + 150px)) translateX(${endX - startX}vw) rotate(${360 + Math.random() * 360}deg);
                      opacity: 0;
                    }
                  }
                `}</style>
              </div>
            );
          })}
        </div>
      )}

      {/* Fireworks Effect - Pháo hoa: nổ ngẫu nhiên khắp trang, thấp hơn */}
      {effectType === 'fireworks' && particleCount > 0 && (
        <div className="fixed inset-0 pointer-events-none z-[1] overflow-hidden">
          {[...Array(Math.max(5, Math.floor(particleCount / 8)))].map((_, i) => {
            const colors = ['#FF6B6B', '#4ECDC4', '#FFE66D', '#FF6B9D', '#C44569', '#95E1D3', '#FFA07A', '#98D8C8'];
            const randomColor = colors[Math.floor(Math.random() * colors.length)];
            const duration = 1.2 + Math.random() * 0.5;
            const totalCycleDuration = duration * 2 + 0.5;
            
            // Tạo 2 vị trí ngẫu nhiên HOÀN TOÀN KHÁC NHAU - nổ khắp màn hình
            const position1X = 5 + (Math.random() * 90); // 5% đến 95% màn hình
            const position1Y = 50 + (Math.random() * 40); // 50% đến 90% chiều cao (nổ thấp)
            const position2X = 5 + (Math.random() * 90);
            const position2Y = 50 + (Math.random() * 40);
            
            return (
              <div key={`firework-${i}`}>
                {/* Quả pháo bay lên từ vị trí ngẫu nhiên */}
                <div
                  className="absolute"
                  style={{
                    left: '0',
                    bottom: '0',
                    animation: `firework-launch-${i} ${totalCycleDuration}s linear ${i * 0.6}s infinite`,
                  }}
                >
                  <div 
                    className="w-2 h-2 rounded-full"
                    style={{
                      background: randomColor,
                      boxShadow: `0 0 10px ${randomColor}`,
                    }}
                  />
                </div>

                {/* Vụ nổ - 12 tia sáng */}
                <div
                  className="absolute"
                  style={{
                    left: '0',
                    top: '0',
                    animation: `firework-explode-position-${i} ${totalCycleDuration}s linear ${i * 0.6}s infinite`,
                  }}
                >
                  {[...Array(12)].map((_, j) => {
                    const angle = (j * 30) * Math.PI / 180;
                    const distance = 50 + Math.random() * 30;
                    const endX = Math.cos(angle) * distance;
                    const endY = Math.sin(angle) * distance + 80;
                    
                    return (
                      <div
                        key={`spark-${j}`}
                        className="absolute w-1.5 h-1.5 rounded-full"
                        style={{
                          background: randomColor,
                          boxShadow: `0 0 8px ${randomColor}`,
                          left: '-3px',
                          top: '-3px',
                          animation: `firework-spark-${i}-${j} ${totalCycleDuration}s ease-out ${i * 0.6}s infinite`,
                        }}
                      />
                    );
                  })}
                </div>

                <style>{`
                  @keyframes firework-launch-${i} {
                    0% {
                      transform: translateX(${position1X}vw) translateY(0);
                      opacity: 1;
                    }
                    ${(duration / totalCycleDuration * 100).toFixed(1)}% {
                      transform: translateX(${position1X}vw) translateY(-${position1Y}vh);
                      opacity: 0;
                    }
                    ${((duration / totalCycleDuration * 100) + 0.1).toFixed(1)}% {
                      transform: translateX(${position2X}vw) translateY(0);
                      opacity: 0;
                    }
                    ${((duration * 2 / totalCycleDuration * 100) + 0.1).toFixed(1)}% {
                      transform: translateX(${position2X}vw) translateY(0);
                      opacity: 1;
                    }
                    100% {
                      transform: translateX(${position2X}vw) translateY(-${position2Y}vh);
                      opacity: 0;
                    }
                  }
                  
                  @keyframes firework-explode-position-${i} {
                    0% {
                      transform: translate(${position1X}vw, calc(100vh - ${position1Y}vh));
                    }
                    ${((duration / totalCycleDuration * 100) + 0.1).toFixed(1)}% {
                      transform: translate(${position2X}vw, calc(100vh - ${position2Y}vh));
                    }
                    100% {
                      transform: translate(${position2X}vw, calc(100vh - ${position2Y}vh));
                    }
                  }
                  
                  ${[...Array(12)].map((_, j) => {
                    const angle = (j * 30) * Math.PI / 180;
                    const distance = 50 + Math.random() * 30;
                    const endX = Math.cos(angle) * distance;
                    const endY = Math.sin(angle) * distance + 80;
                    
                    return `
                      @keyframes firework-spark-${i}-${j} {
                        0% {
                          transform: translate(0, 0) scale(1);
                          opacity: 0;
                        }
                        ${(duration / totalCycleDuration * 100).toFixed(1)}% {
                          transform: translate(0, 0) scale(1);
                          opacity: 0;
                        }
                        ${((duration / totalCycleDuration * 100) + 1).toFixed(1)}% {
                          transform: translate(0, 0) scale(1);
                          opacity: 1;
                        }
                        ${((duration * 2 / totalCycleDuration * 100)).toFixed(1)}% {
                          transform: translate(${endX}px, ${endY}px) scale(0.2);
                          opacity: 0;
                        }
                        100% {
                          transform: translate(0, 0) scale(1);
                          opacity: 0;
                        }
                      }
                    `;
                  }).join('\n')}
                `}</style>
              </div>
            );
          })}
        </div>
      )}

      {/* Hearts Effect - Tăng số lượng, chỉ rơi xuống */}
      {effectType === 'hearts' && particleCount > 0 && (
        <div className="fixed inset-0 pointer-events-none z-[1] overflow-hidden">
          {[...Array(particleCount * 2)].map((_, i) => {
            const startX = Math.random() * 100;
            const endX = startX + (Math.random() * 60 - 30);
            const heartType = Math.random() > 0.5 ? '💗' : '❤️';
            const glowColor = heartType === '💗' ? 'rgba(255,182,193,0.8)' : 'rgba(255,20,147,0.8)';
            
            return (
              <div
                key={`heart-${i}`}
                className="absolute"
                style={{
                  left: `${startX}%`,
                  top: '-50px',
                  animation: `heart-fall-${i} ${6 + Math.random() * 4}s linear ${Math.random() * 5}s infinite`,
                }}
              >
                <div 
                  className="text-2xl md:text-3xl opacity-70"
                  style={{
                    filter: `drop-shadow(0 0 8px ${glowColor})`,
                  }}
                >
                  {heartType}
                </div>
                <style>{`
                  @keyframes heart-fall-${i} {
                    0% {
                      transform: translateY(0) translateX(0) rotate(0deg);
                      opacity: 1;
                    }
                    50% {
                      opacity: 0.8;
                    }
                    100% {
                      transform: translateY(110vh) translateX(${endX - startX}vw) rotate(360deg);
                      opacity: 0;
                    }
                  }
                `}</style>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
